import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { ParkourButtonComponent, ParkourPopupComponent, ParkourToastService } from '@parkour/ui';
import { BarcodeFormat as BarcodeFormatWeb } from '@zxing/library';
import {
  BarcodeFormat as BarcodeFormatNative,
  BarcodeScanner,
} from '@capacitor-mlkit/barcode-scanning';
import { ActivatedRoute, Router } from '@angular/router';
import { isNativeApp } from '../../../../utils';
import { TranslateModule } from '@ngx-translate/core';
import { ParkourBackButtonDirective } from '../../../../shared/directives/parkour-back-button.directive';
import { ParkourHeaderActionComponent } from '../../../../shared/components/parkour-header-action/parkour-header-action.component';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { PageLayoutComponent } from '../../../../shared/components/page-layout/page-layout.component';

import { Camera } from '@capacitor/camera';
import { PermissionService } from '../../../../shared/services/permission.service';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';

@Component({
  templateUrl: './qr-code-scan.page.html',
  standalone: true,
  imports: [
    TranslateModule,
    ParkourBackButtonDirective,
    ParkourHeaderActionComponent,
    ZXingScannerModule,
    ParkourPopupComponent,
    ParkourButtonComponent,
    PageLayoutComponent,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonBackButton,
    IonTitle,
    IonContent,
  ],
})
export class QrCodeScanPage implements AfterViewInit, OnDestroy {
  scanActive = false;
  scanNativeActive = false;
  allowedFormats = [BarcodeFormatWeb.QR_CODE];
  isNoPermissionsPopupOpen = false;
  hasCheckedPermissions = false;
  hasDevices = false;
  hasPermission = false;
  deviceCurrent: MediaDeviceInfo | undefined = undefined;

  constructor(
    private readonly router: Router,
    private readonly parkourToastService: ParkourToastService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly permissionService: PermissionService,
  ) {}

  ngAfterViewInit() {
    if (isNativeApp()) {
      this.startNativeScan();
    }

    this.scanActive = true;
  }

  ngOnDestroy() {
    this.exitScan();
  }

  async startNativeScan() {
    await this.permissionService.withPermissions(
      Camera,
      'camera',
      async () => {
        await this.checkPermissions();
        if (this.hasCheckedPermissions) {
          document.body.classList.add('scanner-active');

          this.scanNativeActive = true;

          BarcodeScanner.scan({ formats: [BarcodeFormatNative.QrCode] })
            .then((result) => {
              if (result.barcodes.length >= 0) {
                this.scanSucces(result.barcodes[0].rawValue);
              } else {
                this.exitScan();
              }
            })
            .catch(() => {
              this.close();
            });
        }
      },
      {
        errorTitle: 'PARKOUR wil je camera gebruiken',
        errorDescription: 'Geef toestemming voor toegang tot je camera in je instellingen.',
      },
    );
  }

  async stopNativeScan() {
    await BarcodeScanner.stopScan();

    document.body.classList.remove('scanner-active');

    this.scanNativeActive = false;
  }

  async checkPermissions() {
    const status = await BarcodeScanner.checkPermissions();

    this.hasDevices = true;

    if (status.camera === 'granted') {
      this.hasCheckedPermissions = true;
      this.onHasPermission(true);
    }

    if (status.camera === 'denied') {
      this.isNoPermissionsPopupOpen = true;
      this.onHasPermission(false);
    }
  }

  async exitScan() {
    if (isNativeApp()) {
      await this.stopNativeScan();
    }

    this.scanActive = false;
  }

  async close() {
    await this.exitScan();

    this.router.navigate(['..'], { relativeTo: this.activatedRoute });
  }

  async openSettings() {
    await BarcodeScanner.openSettings();

    this.hasCheckedPermissions = true;
    this.onHasPermission(true);
  }

  async scanSucces(content: string) {
    await this.exitScan();

    const profielId = content.split('=')[1];

    this.router.navigate(['..'], { queryParams: { profielId }, relativeTo: this.activatedRoute });
  }

  scanError() {
    this.parkourToastService.showToast({
      header: 'Probleem bij scannen',
      content: 'Er was een probleem tijdens het scannen',
      error: true,
    }); // TODO: UX Copy QR toevoegen aan JSON
  }

  onCamerasFound(devices: MediaDeviceInfo[]): void {
    this.hasDevices = Boolean(devices && devices.length);
  }

  onDeviceChange(device: MediaDeviceInfo) {
    this.deviceCurrent = device || undefined;
  }

  onHasPermission(has: boolean) {
    this.hasPermission = has;
  }

  protected readonly isNativeApp = isNativeApp;
}
